import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Trash2, X, Flame, MessageCircle, Calendar, Info } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../utils/apiUrl';

const TYPE_ICON = {
  candela: Flame,
  condoglianza: MessageCircle,
  condoglianza_privata: MessageCircle,
  anniversario: Calendar,
};

const TYPE_COLOR = {
  candela: 'text-amber-500',
  condoglianza: 'text-blue-500',
  condoglianza_privata: 'text-violet-500',
  anniversario: 'text-rose-500',
};

const formatRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'ora';
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} g fa`;
  return d.toLocaleDateString('it-IT');
};

const NotificationBell = () => {
  const { isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  const fetchUnread = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/api/inapp-notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnread(res.data.unread_count || 0);
    } catch { /* ignore */ }
  }, [token]);

  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/inapp-notifications?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications || []);
      setUnread(res.data.unread_count || 0);
    } catch { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchUnread();
    const iv = setInterval(fetchUnread, 60000); // ogni minuto
    return () => clearInterval(iv);
  }, [isAuthenticated, fetchUnread]);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const togglePanel = () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen) fetchList();
  };

  const handleClickNotif = async (n) => {
    try {
      if (!n.read) {
        await axios.put(`${API_URL}/api/inapp-notifications/${n.id}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch { /* ignore */ }
    setOpen(false);
    if (n.link) navigate(n.link);
    fetchUnread();
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/inapp-notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { /* ignore */ }
    fetchList();
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_URL}/api/inapp-notifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { /* ignore */ }
    fetchList();
  };

  if (!isAuthenticated) return null;

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell">
      <button
        onClick={togglePanel}
        className="relative p-2 text-muted-gold hover:text-deep-burgundy transition-colors"
        title="Notifiche"
        data-testid="notification-bell-btn"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full"
            data-testid="notification-unread-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 md:w-96 max-h-[500px] bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden z-50"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-deep-burgundy/5 to-transparent">
            <h3 className="font-serif font-semibold text-charcoal">Notifiche</h3>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-deep-burgundy hover:underline flex items-center gap-1"
                  data-testid="notification-mark-all-read"
                >
                  <Check size={12} /> Segna tutte lette
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-charcoal">
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-400 text-sm">Caricamento...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <Info size={32} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">Nessuna notifica</p>
              </div>
            ) : (
              notifications.map((n) => {
                const Icon = TYPE_ICON[n.tipo] || Bell;
                const colorCls = TYPE_COLOR[n.tipo] || 'text-gray-500';
                return (
                  <div
                    key={n.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleClickNotif(n)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClickNotif(n); } }}
                    className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition flex gap-3 group cursor-pointer outline-none focus:bg-gray-50 ${!n.read ? 'bg-deep-burgundy/[0.03]' : ''}`}
                    data-testid={`notif-${n.id.substring(0, 8)}`}
                  >
                    <div className={`mt-0.5 ${colorCls}`}>
                      <Icon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <p className={`text-sm ${!n.read ? 'font-semibold text-charcoal' : 'text-gray-700'} flex-1`}>
                          {n.titolo}
                        </p>
                        {!n.read && <span className="mt-1 w-2 h-2 bg-deep-burgundy rounded-full shrink-0" />}
                      </div>
                      <p className="text-xs text-gray-500 line-clamp-2">{n.messaggio}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{formatRelative(n.created_at)}</p>
                    </div>
                    <button
                      onClick={(e) => handleDelete(e, n.id)}
                      className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition self-start mt-0.5"
                      title="Elimina"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
